import { Box } from "lucide-react";
import { RENDER_3D_FLAG, useFlag } from "../../lib/flags";
import { requestRender3d, type Render3dFacing } from "../../lib/render3dRequests";
import { loadRender3dEngine } from "../../lib/render3d/loadEngine";
import type { Render3dSource } from "../../lib/events";
import { cn } from "../../lib/cn";

type Variant = "toolbar" | "phone" | "header";

const VARIANT_CLASSES: Record<Variant, string> = {
  toolbar: "h-10 px-3 rounded-2xl text-xs font-semibold gap-2",
  phone: "h-11 px-3 min-[400px]:px-4 rounded-2xl text-body-sm font-semibold gap-2",
  header: "h-11 w-11 md:h-8 md:w-auto md:px-2.5 rounded-xl text-xs font-semibold gap-1.5",
};

interface Props {
  source: Render3dSource;
  variant: Variant;
  /** Classes for the "3D Render" text (e.g. hide it on narrow rows). */
  labelClassName?: string;
  className?: string;
  /** The wall to face when opening (Walls view). */
  getFacing?: () => Render3dFacing | null;
}

/**
 * Opens the 3D walk-through for anyone, signed in or not; the `render_3d`
 * flag hides it entirely. Hover or focus starts loading the engine.
 */
export function Render3DButton({ source, variant, labelClassName, className, getFacing }: Props) {
  const enabled = useFlag(RENDER_3D_FLAG);
  if (!enabled) return null;

  const prefetch = () => {
    loadRender3dEngine().catch(() => {});
  };

  return (
    <button
      type="button"
      onClick={() => requestRender3d(source, getFacing?.() ?? null)}
      onPointerEnter={prefetch}
      onFocus={prefetch}
      aria-label="3D Render"
      title="3D Render"
      data-testid="render-3d-button"
      className={cn(
        "pointer-events-auto shrink-0 inline-flex items-center justify-center whitespace-nowrap text-white shadow-sm ring-1 ring-accent-700 bg-gradient-to-br from-accent-500 to-indigo-600 hover:from-accent-600 hover:to-indigo-700 active:from-accent-700 active:to-indigo-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/50 focus-visible:ring-offset-1",
        VARIANT_CLASSES[variant],
        className
      )}
    >
      <Box className="size-4" aria-hidden />
      <span className={labelClassName}>3D Render</span>
    </button>
  );
}
